import { createContext, useContext, type ReactNode } from 'react';
import { Icon } from './Icon';
import css from './Panel.module.css';

/**
 * How to close what is shown in an overlay the content area opened. A Panel puts the button in its
 * header, a Problem on its side, and the overlay hides its own floating one.
 */
export const CloseContext = createContext<{ close: () => void; label: string } | null>(null);

/**
 * The card the content area draws a widget in: an optional header, the body, and an optional footer
 * against the bottom edge (a hint such as "you can also dictate it": small, out of the way).
 */
export function Panel({ title, subtitle, icon, aside, children, footer, footerIcon, tone = 'default' }: {
  title?: string;
  subtitle?: string;
  icon?: string;
  aside?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  footerIcon?: string;
  tone?: 'default' | 'error';
}) {
  const closing = useContext(CloseContext);
  return (
    <section className={`${css.panel} ${tone === 'error' ? css.error : ''}`}>
      {(title || aside || closing) && (
        <header className={css.header}>
          {icon && <span className={css.icon}><Icon name={icon} size={28} /></span>}
          <div className={css.titles}>
            {title && <h2 className={css.title}>{title}</h2>}
            {subtitle && <p className={css.subtitle}>{subtitle}</p>}
          </div>
          {aside && <div className={css.aside}>{aside}</div>}
          {closing && (
            <button className={css.headerClose} onClick={closing.close} aria-label={closing.label} data-closes="">
              <Icon name="close" size={26} />
            </button>
          )}
        </header>
      )}
      <div className={css.body} tabIndex={0}>{children}</div>
      {footer && <footer className={css.footer}>{footerIcon && <Icon name={footerIcon} size={18} />}{footer}</footer>}
    </section>
  );
}


/**
 * A problem shown where the thing should have been: what, in a short line, and why folded under
 * "details". It always stays inside the box it replaces (a bar, a card): it scrolls, never spills.
 */
export function Problem({ title, detail, detailLabel = 'Details' }: { title: string; detail?: string; detailLabel?: string }) {
  const closing = useContext(CloseContext);
  return (
    <div className={css.problem} role="alert" data-closes={closing ? '' : undefined}>
      <Icon name="warning" size={28} />
      <div className={css.problemText}>
        <strong className={css.problemTitle}>{title}</strong>
        {detail && (
          <details className={css.problemDetails}>
            <summary>{detailLabel}</summary>
            <pre className={css.detail}>{detail}</pre>
          </details>
        )}
      </div>
      {closing && (
        <button className={css.problemClose} onClick={closing.close} aria-label={closing.label}>
          <Icon name="close" size={32} />
        </button>
      )}
    </div>
  );
}
